import * as React from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import I18n, { TranslationsObjectKeys } from "lib/I18n";
import { ClusteringField, PartitionKey } from "../../constants";
import { ExternalLink } from "lib/components/ExternalLink";
import style from "./style.module.css";
import { observer } from "mobx-react-lite";
import { getStore } from "../../store";

interface Document {
  title: string;
  url: string;
}

const bigqueryDocuments: Document[] = [
  {
    title: "ext_url.bigquery_partition_key.title",
    url: "ext_url.bigquery_partition_key.url",
  },
  {
    title: "ext_url.bigquery_clustering_field.title",
    url: "ext_url.bigquery_clustering_field.url",
  },
];

export const AdvancedOptions = observer(() => {
  const { list } = getStore();

  const handleChangePartitionKey = (e: React.FormEvent<HTMLInputElement>) => {
    list.changePartitionKey(e.currentTarget.value as PartitionKey);
  };

  const handleChangeClusteringField = (
    e: React.FormEvent<HTMLInputElement>,
  ) => {
    list.changeClusteringField(e.currentTarget.value as ClusteringField);
  };

  const handleSetDefault = () => {
    list.setDefaultAdvancedOptions();
  };

  const onClickClose = () => {
    list.isAdvancedOptionsOpen = false;
  };

  const renderPartitionKey = () => {
    return (
      <Form.Group as={Row}>
        <Form.Label column sm={3}>
          {I18n.t("event_master_editor.label.partition_key")}:
        </Form.Label>
        <Col sm={9} className={style.radios}>
          {[
            PartitionKey.NcRecordedAt,
            PartitionKey.PostedAt,
            PartitionKey.LcRecordedAt,
          ].map((partitionKey) => (
            <Form.Check
              type="radio"
              name="partitionKey"
              checked={list.partitionKey === partitionKey}
              key={partitionKey}
              value={partitionKey}
              onChange={handleChangePartitionKey}
              inline
              label={partitionKey}
              id={`partition-key-${partitionKey}`}
            />
          ))}
        </Col>
      </Form.Group>
    );
  };

  const renderClusteringField = () => {
    return (
      <Form.Group as={Row}>
        <Form.Label column sm={3}>
          {I18n.t("event_master_editor.label.clustering_field")}:
        </Form.Label>
        <Col sm={9} className={style.radios}>
          {(
            [
              [ClusteringField.Unspecified, "none"],
              [ClusteringField.NsaId, ClusteringField.NsaId],
              [ClusteringField.SerialNumber, ClusteringField.SerialNumber],
              [ClusteringField.DeviceId, ClusteringField.DeviceId],
              [ClusteringField.DeviceId2, ClusteringField.DeviceId2],
            ] as [ClusteringField, string][]
          ).map(([clusteringField, label]) => (
            <Form.Check
              type="radio"
              name="clusteringField"
              checked={list.clusteringField === clusteringField}
              key={clusteringField}
              value={clusteringField}
              onChange={handleChangeClusteringField}
              inline
              label={label}
              id={`clustering-field-${clusteringField}`}
            />
          ))}
        </Col>
      </Form.Group>
    );
  };

  const renderSetDefault = () => {
    return (
      <Form.Group as={Row}>
        <Col sm={{ offset: 3, span: 9 }}>
          <Button variant="light" onClick={handleSetDefault}>
            {I18n.t("event_master_editor.action.set_default")}
          </Button>
        </Col>
      </Form.Group>
    );
  };

  const renderDocument = (documents: { title: string; url: string }[]) => {
    return (
      <Form.Group>
        <Col sm={2}>
          <Form.Label column>
            {I18n.t("event_master_editor.label.document")}
          </Form.Label>
        </Col>
        <Col sm={10}>
          <Form.Group>
            <ul>
              {documents.map((document, i) => (
                <li key={i}>
                  <ExternalLink
                    title={I18n.t(document.title as TranslationsObjectKeys)}
                    url={I18n.t(document.url as TranslationsObjectKeys)}
                  />
                </li>
              ))}
            </ul>
          </Form.Group>
        </Col>
      </Form.Group>
    );
  };

  const renderFooter = () => {
    return (
      <div>
        <Button onClick={onClickClose}>
          {I18n.t("event_master_editor.action.close")}
        </Button>
      </div>
    );
  };

  const renderBody = () => {
    return (
      <Form>
        {renderPartitionKey()}
        {renderClusteringField()}
        {renderSetDefault()}
        {renderDocument(bigqueryDocuments)}
      </Form>
    );
  };

  return (
    <Modal
      animation={false}
      size="lg"
      show={list.isAdvancedOptionsOpen}
      onHide={onClickClose}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {I18n.t("event_master_editor.label.advanced_options")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ overflow: "auto" }}>{renderBody()}</Modal.Body>
      <Modal.Footer>{renderFooter()}</Modal.Footer>
    </Modal>
  );
});
